import type { GenericEvent, CardBlogResult } from '~/sanity/types';
import type { SolrFacetCount } from '~/utils/solr/SolrQuery';
import SolrQuery from '~/utils/solr/SolrQuery';
import SolrFq from '~/utils/solr/SolrFq';
import SolrSort from '~/utils/solr/SolrSort';

export type EventFormatValue = Exclude<GenericEvent['eventFormat'], 'hybrid'>; // hybrid events are not supported in the filter

export type EventFormatOption = {
  value: EventFormatValue;
  label: string;
  count: number;
};

export type EventPaginationResult = {
  items: Array<NonNullable<CardBlogResult>>;
  total: number;
};

export type EventPageChangedPayload = {
  pageNumber: number;
};

const eventContentTypes = ['genericEvent', 'lumaEvent', 'linkedEvent'] as const;
const eventFormatField = 'eventFormat_ss';
const eventFormatTag = 'fmt';
const eventFormatOrder: Array<EventFormatValue> = ['virtual', 'inPerson'];
const eventFormatLabels: Record<EventFormatValue, string> = {
  virtual: 'Virtual',
  inPerson: 'In-person',
};

const parseFormatParam = (raw: unknown): EventFormatValue | null => {
  if (raw === 'virtual' || raw === 'inPerson') {
    return raw;
  }
  return null;
};

const getEventPagination = async ({
  pageNumber,
  pageLimit,
  format,
}: {
  pageNumber: number;
  pageLimit: number;
  format: EventFormatValue | null;
}): Promise<{ result: EventPaginationResult; facetCounts: Array<SolrFacetCount> }> => {
  const fq = SolrFq.or().pushGroup('_type_s', { fieldValues: [...eventContentTypes] });
  fq.excludePastDates('articleDate_dt');
  if (format) {
    fq.pushGroup(eventFormatField, { fieldValues: [format], tag: eventFormatTag });
  }

  const query = SolrQuery.resourceCenter()
    .setFq(fq)
    .setSort(SolrSort.articleDate({ order: 'asc' }))
    .setFacet({ field: eventFormatField, excludeTag: eventFormatTag })
    .setRange({
      start: (pageNumber - 1) * pageLimit,
      rows: pageLimit,
    });

  const response = await useSolrQuery<CardBlogResult>({ q: query });

  return {
    result: {
      items: response.data.value,
      total: response.numFound.value,
    },
    facetCounts: response.facetCounts.value,
  };
};

export const useEventFilter = async () => {
  const router = useRouter();
  const route = useRoute();
  const { toTopOfWindow } = useWwwScroll();

  const initialFormat = parseFormatParam(route.query.format);
  const initialPage = route.query.page ? parseInt(route.query.page as string, 10) : 1;

  const selectedFormat = ref<EventFormatValue | null>(initialFormat);
  const pageNumber = ref(initialPage);
  const pageLimit = ref(24);
  const isLoading = ref(true);

  const initial = await getEventPagination({
    pageNumber: pageNumber.value,
    pageLimit: pageLimit.value,
    format: selectedFormat.value,
  });
  const paginationResult = ref<EventPaginationResult>(initial.result);
  const facetCounts = ref<Array<SolrFacetCount>>(initial.facetCounts);
  const formatOptions = computed<Array<EventFormatOption>>(() => {
    const options: Array<EventFormatOption> = [];
    for (const value of eventFormatOrder) {
      const bucket = facetCounts.value.find((facet) => facet.value === value);
      if (!bucket) {
        continue;
      }
      options.push({ value, label: eventFormatLabels[value], count: bucket.count });
    }
    return options;
  });
  isLoading.value = false;
  const syncUrl = (): void => {
    router.replace({
      path: route.path,
      query: {
        format: selectedFormat.value ?? undefined,
        page: pageNumber.value > 1 ? String(pageNumber.value) : undefined,
      },
    });
  };

  const runQuery = async (): Promise<void> => {
    isLoading.value = true;
    try {
      const next = await getEventPagination({
        pageNumber: pageNumber.value,
        pageLimit: pageLimit.value,
        format: selectedFormat.value,
      });
      paginationResult.value = next.result;
      facetCounts.value = next.facetCounts;
    } finally {
      isLoading.value = false;
    }
  };

  const onFormatChange = async (format: EventFormatValue | null): Promise<void> => {
    if (isLoading.value) {
      return;
    }
    selectedFormat.value = format;
    pageNumber.value = 1;
    syncUrl();
    await runQuery();
    toTopOfWindow();
  };

  const onPageChange = async (event: EventPageChangedPayload): Promise<void> => {
    if (isLoading.value) {
      return;
    }
    pageNumber.value = event.pageNumber;
    syncUrl();
    await runQuery();
    toTopOfWindow();
  };

  return {
    isLoading,
    pageNumber,
    pageLimit,
    paginationResult,
    selectedFormat,
    formatOptions,
    onFormatChange,
    onPageChange,
  };
};
